<template>
  <div class="note-tile">
    <div class="note-header">
      <div class="btn-group" v-if="!commentsMode">
        <a v-for="type in availableTypes" class="btn btn-sm btn-light" :class="{active: type.value === noteType}" @click.prevent="setType(type.value)">
          <i :class="'fa fa-' + type.icon"></i>
          {{type.name}}
        </a>
      </div>

      <div class="btn-group pull-right">
        <a class="btn btn-sm btn-light" :class="{active: previewing}" @click.prevent="togglePreview">
          Preview
        </a>
        <a class="btn btn-sm btn-light" :class="{active: helping}" @click.prevent="helping = !helping">
          Formatting help
        </a>
      </div>
      <div class="clearfix"></div>
    </div>

    <div class="row">
      <div class="col-sm">
        <div class="note-form">
          <div v-if="autocomplete.show" class="list-group autocomplete" :style="{ 'top': autocomplete.margin + 'px'}">
            <a v-for="option, i in filteredAutocompleteOptions"
               href="#"
               class="list-group-item list-group-item-action"
               :class="{ 'active': i === autocomplete.selectedOption }"
               @click.prevent="replaceWordAtCursor(option.text); hideAutocomplete();"
            >{{option.label}}</a>
          </div>
          <textarea
            v-if="!previewing"
            v-model="markdown"
            ref="textarea"
            :style="{height: textareaHeight + 'px'}"
            @input="handleKey"
            @keydown.up="handleUp"
            @keydown.down="handleDown"
            @keydown.enter.exact="handleEnter"
          >
          </textarea>
          <div v-if="previewing" v-html="previewHtml" class="note-html note-html-preview"></div>
          <a class="btn btn-sm btn-primary" href="#" @click.prevent="save">
            Save
          </a>
          <a class="btn btn-sm btn-light" href="#" @click.prevent="cancel" tabindex="-1">
            Cancel
          </a>
        </div>
      </div>
    </div>

    <div v-if="helping" class="md-help">
      <p>
        Comments use <a href="https://en.wikipedia.org/wiki/Markdown" target="_blank">markdown</a> syntax with some extra tweaks.
        Hit return twice for a new line.
      </p>
      <table class="table">
        <tbody>
          <tr>
            <td>
              *italics*
              <br/>**bold**
              <br/>***bold italics***
            </td>
            <td>
              <em>italics</em>
              <br/><b>bold</b>
              <br/><b><em>bold italics</em></b>
            </td>
          </tr>
          <tr>
            <td>[a link](https://example.com)</td>
            <td>
              <a href="https://example.com" target="_blank">a link</a>
            </td>
          </tr>
          <tr>
            <td>
              Link to task #T30
            </td>
            <td>
              Link to task <a href="javascript:">#T30</a>
            </td>
          </tr>
          <tr>
            <td>
              * one
              <br>* two
              <br>* three
            </td>
            <td>
              <ul>
                <li>one</li>
                <li>two</li>
                <li>three</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              # Heading 1
              <br/>## Heading 2
              <br/>### Heading 3
            </td>
            <td>
              <h1>Heading 1</h1>
              <h2>Heading 2</h2>
              <h3>Heading 3</h3>
            </td>
          </tr>
          <tr>
            <td>
              ```
              <br/>print "source code"
              <br/>2**32 - 1
              <br/>```
            </td>
            <td>
              <pre><code>
print "source code"
2**32 - 1
              </code></pre>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
 import axios from 'axios';

 const minTextareaHeight = 128;

 export default {
   props: {
     commentsMode: {
       type: Boolean,
       default: false,
     },
     initialMarkdown: {
       type: String,
       default: '',
     },
     initialType: {
       /* type: Number, */
       /* enforce type when database column is a integer */
       default: 0,
     }
   },
   data() {
     return {
       noteType: parseInt(this.initialType),
       markdown: this.initialMarkdown,
       previewing: false,
       helping: false,
       previewHtml: "",
       textareaHeight: minTextareaHeight,
       autocomplete: {
         tasks: [],
         discussions: [],
         searchType: "",
         search: "",
         replaceStart: 0,
         replaceEnd: 0,
         show: false,
         selectedOption: 0,
         margin: 0,
       },
     };
   },

   computed: {
     availableTypes() {
       return [
         {name: 'Note', value: 0, icon: 'pencil'},
         {name: 'Email', value: 1, icon: 'envelope'},
         {name: 'Call', value: 2, icon: 'phone'},
         {name: 'Meeting', value: 3, icon: 'user'},
         {name: 'Message', value: 4, icon: 'mobile'},
       ];
     },
     filteredAutocompleteOptions() {
       let options = this.autocomplete.searchType === 't' ?
                     this.autocomplete.tasks :
                     this.autocomplete.discussions;
       const search = this.autocomplete.search.toLowerCase();
       if (search) {
         options = options.filter(function(item) {
           return item.search.includes(search);
         }).slice(0, 5)
       }
       return options.slice(0, 5);
     }
   },

   methods: {
     setType(type) {
       this.noteType = type;
     },
     badgeClass(type) {
       var badgeClasses = [
         'fa-pencil',
         'fa-envelope',
         'fa-phone',
         'fa-user',
         'fa-mobile',
       ];

       return badgeClasses[type];
     },
     reset() {
       this.markdown = '';
       this.previewing = false;
       this.helping = false;
     },
     focus() {
       this.$refs.textarea.focus();
     },
     save() {
       this.$emit('save', {
         markdown: this.markdown,
         noteType: this.noteType,
       });
     },
     showAutocomplete() {
       switch(this.autocomplete.searchType) {
         case "t":
           if (!this.autocomplete.tasks.length) {
             axios.get('/api/tasks/project/' + this.$store.getters.currentProjectId)
                  .then((response) => {
                    this.autocomplete.tasks = response.data.tasks.map((task) => {
                      return {
                        'text': '#T'+task.taskNumber,
                        'search': task.taskNumber.toString()+task.title.toLowerCase().replace(/\s/g, ''),
                        'label': 'T'+task.taskNumber+' '+task.title,
                      };
                    });
                  });
           }
           break;
         case "d":
           if (!this.autocomplete.discussions.length) {
             axios.get('/api/discussions/project/' + this.$store.getters.currentProjectId)
                  .then((response) => {
                    this.autocomplete.discussions = response.data.posts.map((discussion) => {
                      return {
                        'text': '#D'+discussion.postNumber,
                        'search': discussion.postNumber.toString()+discussion.title.toLowerCase().replace(/\s/g, ''),
                        'label': 'D'+discussion.postNumber+' '+discussion.title,
                      };
                    });
                  });
           }
           break;
         default:
           return
       };
       this.autocomplete.show = true;
       this.autocomplete.selectedOption = 0;
     },
     hideAutocomplete() {
       this.autocomplete.show = false;
       this.autocomplete.selectedOption = 0;
       this.focus();
     },
     currentWord(text, position) {
       while (position > 0 && text.charAt(position - 1).match(/\S/)) {
         position--;
       }
       let word = '';
       const wordStart = position;
       while (position < text.length && text.charAt(position).match(/\S/)) {
         word += text.charAt(position);
         position++;
       }
       return [wordStart, word];
     },
     getHeightOfCharacter(index) {
       const linesUpToCursor = this.markdown
                                   .substring(0, index)
                                   .split(/\r\n|\r|\n/)
                                   .length;
       const lineHeight = 18;
       return linesUpToCursor * lineHeight;
     },
     handleKey($event) {
       const textareaContentHeight = this.getHeightOfCharacter(this.markdown.length);
       this.textareaHeight = textareaContentHeight < minTextareaHeight ? minTextareaHeight : textareaContentHeight;
       const [wordStart, currentWord] = this.currentWord(this.markdown, $event.target.selectionStart);
       if (!currentWord.startsWith('#') || !currentWord[1] || !['t', 'd'].includes(currentWord[1].toLowerCase())) {
         this.hideAutocomplete();
         return;
       }

       this.autocomplete.searchType = currentWord[1].toLowerCase();
       this.autocomplete.search = currentWord.substring(2);
       this.autocomplete.replaceStart = wordStart;
       this.autocomplete.replaceEnd = wordStart + currentWord.length;

       const margin = this.getHeightOfCharacter(this.autocomplete.replaceEnd) + 24;
       this.autocomplete.margin = margin > this.textareaHeight ? this.textareaHeight : margin;
       this.showAutocomplete();
     },
     handleEnter(e) {
       if (!this.autocomplete.show) {
         return;
       }
       e.preventDefault();
       const option = this.filteredAutocompleteOptions[this.autocomplete.selectedOption];
       if (option) {
         this.replaceWordAtCursor(option.text);
       }
       this.hideAutocomplete();
     },
     handleUp(e) {
       if (!this.autocomplete.show) {
         return;
       }
       e.preventDefault();
       this.autocomplete.selectedOption = this.autocomplete.selectedOption <= 0 ? 0 : this.autocomplete.selectedOption - 1;
     },
     handleDown(e) {
       if (!this.autocomplete.show) {
         return;
       }
       e.preventDefault();
       this.autocomplete.selectedOption = this.autocomplete.selectedOption >= this.filteredAutocompleteOptions.length - 1 ?
                                          this.filteredAutocompleteOptions.length - 1 :
                                          this.autocomplete.selectedOption + 1;
     },
     replaceWordAtCursor(text) {
       let before = this.markdown.substring(0, this.autocomplete.replaceStart)
       let after = this.markdown.substring(this.autocomplete.replaceEnd);
       this.markdown = before + text + " " + after;
     },
     cancel() {
       this.$emit('cancel');
     },
     togglePreview() {
       let that = this;
       this.previewing = !this.previewing;

       if (this.previewing) {
         var currentProjectId = this.$store.getters.currentProjectId;

         // Tail is defined such that if there is no project id then we pass an empty string otherwise we need to add
         // a backslash before the key. This is because with optional parameters it seems /markdown will match
         // however /markdown/ will not
         var tail = (currentProjectId ? "?project_id=" + currentProjectId : "");

         axios.post('/markdown' + tail, {
           markdown: that.markdown,
         }).then(function(response) {
           that.previewHtml = response.data.html;
         });
       }
       
     }
   }
 };
</script>



// WEBPACK FOOTER //
// assets/js/components/NoteForm.vue